import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiTrash2 } from 'react-icons/fi';
import { adminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { hasAdminManagerRole } from '../../../utils/roles';

// Warehouse transfer: move in-stock units between internal warehouses.
// Strictly physical -- units move FIFO (oldest first) from the source.
// Storage billing follows the move: source pallet footprints close when
// emptied, and the destination starts billing at its own rates from today.
const REASON_OPTIONS = [
  { value: 'relocation', label: 'Warehouse relocation / rebalancing' },
  { value: 'wrong_warehouse_received', label: 'Received into the wrong warehouse' },
  { value: 'customer_request', label: 'Customer request' },
  { value: 'other', label: 'Other' }
];

const CreateTransfer = () => {
  const { user } = useAuth();
  const canCorrect = hasAdminManagerRole(user);
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState('');
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState([]);
  const [reasonCode, setReasonCode] = useState('relocation');
  const [customerReason, setCustomerReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const { data: customersData } = useQuery(
    'customers-filter-options',
    () => adminAPI.getCustomers({ limit: 500 }),
    { staleTime: 5 * 60 * 1000 }
  );
  const customers = customersData?.customers || [];

  const { data: warehousesData } = useQuery(
    'warehouses-all',
    () => adminAPI.getWarehouses(),
    { staleTime: 5 * 60 * 1000 }
  );
  const warehouses = warehousesData?.data || warehousesData?.warehouses || (Array.isArray(warehousesData) ? warehousesData : []);

  // Source stock drives the picker: you can only transfer what the source
  // warehouse actually tracks in stock.
  const stockStatusQuery = useQuery(
    ['adjustment-stock-status', companyId, fromWarehouseId, search],
    () => adminAPI.getAdjustmentStockStatus({ companyId, warehouseId: fromWarehouseId, search }),
    { enabled: !!companyId && !!fromWarehouseId, keepPreviousData: true }
  );
  const stockRows = (stockStatusQuery.data?.data || []).filter((row) => row.asset_count > 0);

  const addLine = (row) => {
    if (lines.some((line) => line.inventoryItemId === row.id)) return;
    setLines([...lines, {
      inventoryItemId: row.id,
      sku: row.product_sku,
      name: row.product_name,
      available: row.asset_count,
      quantity: ''
    }]);
  };

  const updateLineQuantity = (index, value) => {
    const next = [...lines];
    next[index].quantity = value === '' ? '' : String(parseInt(value, 10) || '');
    setLines(next);
  };

  const removeLine = (index) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const createMutation = useMutation({
    mutationFn: (payload) => adminAPI.createTransfer(payload),
    onSuccess: (result) => {
      const review = result?.correction?.requiresBillingReview;
      toast.success(`${result?.message || 'Transfer applied.'}${review ? ' Flagged for billing review.' : ''}`);
      navigate(`/admin/corrections/${result.correction.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to apply transfer');
    }
  });

  const validLines = lines.filter((line) => {
    const quantity = parseInt(line.quantity, 10);
    return Number.isInteger(quantity) && quantity > 0 && quantity <= line.available;
  });
  const canSubmit = companyId && fromWarehouseId && toWarehouseId &&
    fromWarehouseId !== toWarehouseId &&
    validLines.length > 0 && validLines.length === lines.length &&
    !createMutation.isLoading;

  const handleSubmit = () => {
    createMutation.mutate({
      companyId,
      fromWarehouseId,
      toWarehouseId,
      reasonCode,
      customerReason: customerReason.trim() || null,
      internalNotes: internalNotes.trim() || null,
      lines: validLines.map((line) => ({
        inventoryItemId: line.inventoryItemId,
        quantity: parseInt(line.quantity, 10)
      }))
    });
  };

  const resetContext = () => {
    setLines([]);
    setSearch('');
  };

  // Correction mutations are role-gated on the backend (RF15:
  // admin/manager) -- this form would only fail at submission.
  if (!canCorrect) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
          Inventory corrections require admin or manager privileges.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <Link to="/admin/corrections" className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 mb-4">
        <FiArrowLeft className="mr-1 h-4 w-4" /> Back to corrections
      </Link>

      <h1 className="text-2xl font-semibold text-gray-900 mb-1">New Warehouse Transfer</h1>
      <p className="text-sm text-gray-500 mb-6">
        Move in-stock units between warehouses. Units move oldest-first; storage billing
        stops at the source and starts at the destination at its own rates.
      </p>

      <div className="bg-white shadow rounded-lg p-4 mb-6 flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Customer</label>
          <select
            value={companyId}
            onChange={(e) => { setCompanyId(e.target.value); resetContext(); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[220px]"
          >
            <option value="">Select customer...</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From warehouse</label>
          <select
            value={fromWarehouseId}
            onChange={(e) => { setFromWarehouseId(e.target.value); resetContext(); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[180px]"
          >
            <option value="">Select source...</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To warehouse</label>
          <select
            value={toWarehouseId}
            onChange={(e) => setToWarehouseId(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[180px]"
          >
            <option value="">Select destination...</option>
            {warehouses.filter((warehouse) => warehouse.id !== fromWarehouseId).map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </div>
      </div>

      {companyId && fromWarehouseId && (
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="p-4 border-b border-gray-200">
            <label className="block text-xs font-medium text-gray-500 mb-1">Add items (in stock at source)</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SKU or name..."
              className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
            {stockRows.map((row) => {
              const added = lines.some((line) => line.inventoryItemId === row.id);
              return (
                <button
                  key={row.id}
                  onClick={() => addLine(row)}
                  disabled={added}
                  className={`w-full px-4 py-2 flex items-center justify-between text-left text-sm ${added ? 'bg-gray-50 text-gray-400' : 'hover:bg-indigo-50'}`}
                >
                  <span>
                    <span className="font-medium">{row.product_sku}</span>
                    {row.product_name ? <span className="text-gray-500"> — {row.product_name}</span> : null}
                  </span>
                  <span className="text-xs text-gray-500">
                    Available: <span className="font-medium text-gray-900">{row.asset_count}</span>
                    {added && <span className="ml-2 text-gray-400">added</span>}
                  </span>
                </button>
              );
            })}
            {!stockStatusQuery.isLoading && stockRows.length === 0 && (
              <p className="px-4 py-4 text-sm text-gray-500">No in-stock items at this warehouse.</p>
            )}
          </div>
        </div>
      )}

      {lines.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer Qty</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {lines.map((line, index) => {
                const quantity = parseInt(line.quantity, 10);
                const overAvailable = Number.isInteger(quantity) && quantity > line.available;
                return (
                  <tr key={line.inventoryItemId}>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium text-gray-900">{line.sku}</span>
                      {line.name ? <span className="text-gray-500"> — {line.name}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{line.available}</td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={line.quantity}
                        onChange={(e) => updateLineQuantity(index, e.target.value)}
                        className={`w-24 border rounded-md px-2 py-1 text-sm text-right ${overAvailable ? 'border-red-400 text-red-700' : 'border-gray-300'}`}
                      />
                      {overAvailable && (
                        <p className="text-xs text-red-600 mt-1">Exceeds available units</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeLine(index)} className="text-gray-400 hover:text-red-600" aria-label="Remove line">
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lines.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[280px]"
            >
              {REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer-facing reason <span className="text-gray-400 font-normal">(optional, visible to the customer)</span>
            </label>
            <textarea
              value={customerReason}
              onChange={(e) => setCustomerReason(e.target.value)}
              rows={2}
              maxLength={2000}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Internal notes <span className="text-gray-400 font-normal">(staff only)</span>
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={2}
              maxLength={5000}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Transferring...' : 'Apply Transfer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateTransfer;
